import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Data } from 'slate';
import isHotkey from 'is-hotkey';

const INDENTATION_DATA_FIELD = 'level';
const DEFAULT_MAX_INDENTATION_LEVEL = 10;
export const INDENTATION_BLOCK_TYPE = 'indentBlock';

export const IndentationBlock = ({ attributes, children, node }) => {
  const level = node.data.get(INDENTATION_DATA_FIELD);

  return (
    <div
      className={classnames('indentationBlock', `indentationBlock--level${level}`)}
      {...attributes}
    >
      {children}
    </div>
  );
};

const INDENT_SIZE = 20;

IndentationBlock.propTypes = {
  children: PropTypes.node,
  attributes: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  node: PropTypes.shape({
    data: PropTypes.shape({
      get: PropTypes.func,
    }),
  }),
};

const verifyBlock = (currentBlock, blacklist = [], whitelist = []) => {
  if (blacklist.includes(currentBlock.type)) {
    return false;
  }
  if (whitelist.length && !whitelist.includes(currentBlock.type)) {
    return false;
  }

  return true;
};

const verifyBlocks = (blocks, blacklist = [], whitelist = []) =>
  blocks.reduce((allowed, block) => allowed && verifyBlock(block, blacklist, whitelist), true);

const indentationPlugin = ({
  indentableBlacklist = [],
  indentableWhitelist = [],
  maxIndentation = DEFAULT_MAX_INDENTATION_LEVEL,
} = {}) => ({
  onKeyDown: (event, editor, next) => {
    const { selection, startBlock } = editor.value;

    if (isHotkey('tab+shift', event)) {
      event.preventDefault();
      event.stopPropagation();
      return editor.decreaseIndentation();
    }

    if (isHotkey('tab', event)) {
      event.preventDefault();
      event.stopPropagation();

      if (!verifyBlocks(editor.value.blocks, indentableBlacklist, indentableWhitelist)) {
        return;
      }
      return editor.increaseIndentation();
    }

    if (isHotkey('backspace', event) && selection.start.offset === 0 && !selection.isExpanded) {
      event.preventDefault();
      const currentIndentation = editor.getIndentation();
      if (!currentIndentation) {
        return next();
      }
      return editor.decreaseIndentation();
    }

    if (isHotkey('enter', event)) {
      event.preventDefault();
      if (selection.isExpanded) {
        editor.delete();
      }
      const currentIndentation = editor.getIndentation();
      if (currentIndentation) {
        const currentBlockKey = startBlock.key;
        editor.unwrapBlock(INDENTATION_BLOCK_TYPE);
        editor.splitBlock();
        return editor.wrapBlockByKey(
          currentBlockKey,
          {
            type: INDENTATION_BLOCK_TYPE,
            data: Data.fromJS({
              [INDENTATION_DATA_FIELD]: currentIndentation,
            }),
          }
        );
      }
    }
    next();
  },
  renderBlock: (props, editor, next) => {
    if (props.node.type === INDENTATION_BLOCK_TYPE) {
      return <IndentationBlock {...props}/>;
    }
    return next();
  },
  commands: {
    setIndentation: (editor, level) => {
      editor.unwrapBlock(INDENTATION_BLOCK_TYPE);

      if (level < 0) {
        level = 0;
      }
      if (level > maxIndentation) {
        level = maxIndentation;
      }

      if (level > 0) {
        editor.wrapBlock({
          type: INDENTATION_BLOCK_TYPE,
          data: Data.fromJS({ [INDENTATION_DATA_FIELD]: level }),
        });
      }
    },
    increaseIndentation: editor => {
      const currentIndentation = editor.getIndentation();
      if (currentIndentation + 1 <= maxIndentation) {
        editor.setIndentation(currentIndentation + 1);
      }
    },
    decreaseIndentation: editor => {
      const currentIndentation = editor.getIndentation();
      if (currentIndentation > 0) {
        editor.setIndentation(currentIndentation - 1);
      }
    },
  },
  queries: {
    getIndentation: editor => {
      const closestIndentation = editor.value.document.getClosest(
        editor.value.startBlock.key,
        node => node.type === INDENTATION_BLOCK_TYPE
      );

      return closestIndentation ? closestIndentation.data.get(INDENTATION_DATA_FIELD) : 0;
    },
  },
  serialize: (node, children) => {
    if (node.type === INDENTATION_BLOCK_TYPE) {
      const level = node.data.get('level');
      return (<div style={{ marginLeft: `${level * INDENT_SIZE}px` }}>{children}</div>);
    }
  },
});

export default indentationPlugin;
